import dayjs from "dayjs";

export type DocumentSource =
  | "UPLOAD"
  | "GENERATE"
  | "SEND"
  | "SIGNATURE"
  | string;

export interface DocumentAuditMeta {
  id: string | number | null;
  documentType: string | null;
  documentTypeLabel: string | null;
  uploadedAt: string | null;
  uploadedAtLabel: string | null;
  uploaderName: string | null;
  uploaderRole: string | null;
  uploadedById: string | number | null;
  source: DocumentSource | null;
  hasUploader: boolean;
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  IdDocument: "ID Document",
  ApplicationDocument: "Application Document",
  PolicyDocument: "Policy Document",
  Other: "Other",
};

export const formatDocumentTypeLabel = (
  type: string | null | undefined
): string | null => {
  if (type == null || String(type).trim() === "") return null;
  const key = String(type).trim();
  return DOCUMENT_TYPE_LABELS[key] ?? key;
};

const pickFirstString = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (value == null) continue;
    const trimmed = String(value).trim();
    if (trimmed.length > 0) return trimmed;
  }
  return null;
};

const resolveUploader = (
  document: any
): { name: string | null; role: string | null; id: string | number | null } => {
  const user =
    document?.UploadedBy ??
    document?.CreatedBy ??
    document?.User ??
    document?.uploadedBy ??
    document?.createdBy ??
    null;

  const uploadedById =
    document?.UploadedByID ??
    document?.uploadedById ??
    document?.UploadedById ??
    (typeof user === "object" && user != null
      ? user.id ?? user.Id ?? null
      : null);

  if (user == null) {
    if (uploadedById != null && String(uploadedById).trim() !== "") {
      return {
        name: `User #${uploadedById}`,
        role: null,
        id: uploadedById,
      };
    }
    return { name: null, role: null, id: null };
  }

  // Scalar id only (claim payloads sometimes return UploadedBy: 3)
  if (
    typeof user === "number" ||
    (typeof user === "string" && /^\d+$/.test(String(user).trim()))
  ) {
    const id = typeof user === "number" ? user : String(user).trim();
    return {
      name: `User #${id}`,
      role: null,
      id: uploadedById ?? id,
    };
  }

  if (typeof user === "string") {
    return { name: user, role: null, id: uploadedById };
  }

  const name =
    pickFirstString(
      user.Name,
      user.name,
      [user.FirstName ?? user.firstName, user.Surname ?? user.surname]
        .filter(Boolean)
        .join(" ")
        .trim() || null,
      user.Email,
      user.email
    ) ??
    (uploadedById != null && String(uploadedById).trim() !== ""
      ? `User #${uploadedById}`
      : null);

  return {
    name,
    role: pickFirstString(
      user.Role,
      user.UserRole,
      user.role,
      user.userRole,
      user?.Role?.Name,
      user?.role?.Name
    ),
    id: uploadedById ?? user.id ?? user.Id ?? null,
  };
};

export const getDocumentAuditMeta = (document: any): DocumentAuditMeta => {
  const uploadedAt = pickFirstString(
    document?.UploadedAt,
    document?.CreatedAt,
    document?.createdAt,
    document?.updatedAt,
    document?.UpdatedAt
  );

  const { name, role, id: uploadedById } = resolveUploader(document);
  const source = pickFirstString(
    document?.Source,
    document?.source,
    document?.EventType,
    document?.eventType
  );
  const documentType = pickFirstString(
    document?.DocumentType,
    document?.documentType,
    document?.Type,
    document?.DocumentKey,
    document?.documentKey
  );

  return {
    id: document?.id ?? document?.Id ?? null,
    documentType,
    documentTypeLabel: formatDocumentTypeLabel(documentType),
    uploadedAt,
    uploadedAtLabel: uploadedAt
      ? dayjs(uploadedAt).isValid()
        ? dayjs(uploadedAt).format("YYYY-MM-DD HH:mm")
        : uploadedAt
      : null,
    uploaderName: name,
    uploaderRole: role,
    uploadedById,
    source: source as DocumentSource | null,
    hasUploader: !!name,
  };
};

export const DOCUMENT_AUDIT_ACTIONS = [
  "DOCUMENT_UPLOADED",
  "DOCUMENT_GENERATED",
  "DOCUMENT_SENT",
  "DOCUMENT_SMS_SENT",
  "DOCUMENT_DELETED",
  "APPLICATION_SMS_SENT",
  "NEW_POLICY_SMS_SENT",
  "POLICY_SMS_SENT",
  "DocumentUploaded",
  "DocumentGenerated",
  "DocumentSent",
  "DocumentSmsSent",
] as const;

export const isDocumentAuditAction = (
  action: string | null | undefined
): boolean => {
  const value = String(action ?? "").trim();
  if (!value) return false;
  const upper = value.toUpperCase();
  if (DOCUMENT_AUDIT_ACTIONS.some((a) => a.toUpperCase() === upper)) return true;
  return (
    upper.includes("DOCUMENT") ||
    upper.includes("SMS_SENT") ||
    upper.includes("SMSSENT")
  );
};

const knownUserDisplay = (knownUser: any) => {
  if (!knownUser) return null;
  const id = knownUser.id ?? knownUser.Id;
  const name = pickFirstString(
    knownUser.Name,
    knownUser.name,
    [knownUser.FirstName, knownUser.Surname].filter(Boolean).join(" ").trim() ||
      null,
    knownUser.Email,
    knownUser.email
  );
  const role = pickFirstString(
    typeof knownUser.Role === "string" ? knownUser.Role : null,
    knownUser.UserRole,
    knownUser?.Role?.Name,
    knownUser?.Role?.Role,
    knownUser.role
  );
  if (id == null || !name) return null;
  return { id, name, role };
};

/**
 * Claim GET often returns UploadedByID without nested UploadedBy.Name.
 * Fill Name/Role from known users (current user, audit trail users, etc.).
 */
export const enrichDocumentsWithKnownUsers = (
  documents: any[],
  knownUsers: any[]
): any[] => {
  if (!Array.isArray(documents)) return [];
  const byId = new Map<string, { id: any; name: string; role: string | null }>();
  (knownUsers || []).forEach((u) => {
    const display = knownUserDisplay(u);
    if (display) byId.set(String(display.id), display);
  });
  if (byId.size === 0) return documents;

  return documents.map((doc) => {
    const meta = getDocumentAuditMeta(doc);
    const alreadyNamed =
      meta.hasUploader && !String(meta.uploaderName ?? "").startsWith("User #");
    if (alreadyNamed) return doc;

    const docUserId =
      meta.uploadedById ??
      doc?.UploadedByID ??
      doc?.UploadedBy?.id ??
      doc?.UploadedBy?.Id;
    if (docUserId == null) return doc;

    const known = byId.get(String(docUserId));
    if (!known) return doc;

    return {
      ...doc,
      UploadedBy: {
        ...(typeof doc.UploadedBy === "object" && doc.UploadedBy
          ? doc.UploadedBy
          : {}),
        id: known.id,
        Name: known.name,
        Role: known.role ?? doc?.UploadedBy?.Role,
      },
    };
  });
};
